import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Enumerable from 'linq';
import { forkJoin, Observable, Subscription, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AddEditConferencePurchaseOrderViewModel, ConferencePurchaseOrderTotalsViewModel, ConferenceViewModel, PromoCodeCheckViewModel, RegistrationPackageViewModel, StringViewModel } from 'src/app/_models/generatedModels';
import { AnalyticsService } from 'src/app/_services/analytics.service';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ConferenceCMSService, EventService, OrganizationService, StaticDataService } from 'src/app/_services/generatedServices';
import { NavigationService } from 'src/app/_services/navigation-service';
import { ToasterService } from 'src/app/_services/toaster.service';
import { GroupRegistrationRegistrantModalComponent } from './group-registration-registrant-modal.component';
import { RegistrationHelperService } from 'src/app/_services/registration-helper.service';
import { ConferenceGroupDiscountsModalComponent } from './conference-group-discounts-modal.component';


@Component({
  selector: 'bxl-group-registration',
  templateUrl: 'group-registration.component.html',
})
export class GroupRegistrationComponent implements OnInit {
  initialized = false;
  private parentRouteSubscription: Subscription;
  conferenceSlug: any;
  submitComplete: Promise<{}> | undefined;
  public promoCode: string;
  conference: ConferenceViewModel;
  currencyCode: string;
  @ViewChild('theForm', {static: false}) public userFrm: NgForm;
  errorMessage: string;
  editError: string;
  promoError: string;
  packages: RegistrationPackageViewModel[];
  groupModel: AddEditConferencePurchaseOrderViewModel;
  hasGroupDiscounts: boolean = false;
  totals: ConferencePurchaseOrderTotalsViewModel;
  public cardNumber: string;
  public expMo: string;
  public expYr: string;
  public cvv: string;
  linkUniqueId: string = null;


  constructor(private analyticsService: AnalyticsService, private cmsService: ConferenceCMSService, private modalService: NgbModal, private router: Router, private zone: NgZone, private eventService: EventService, private orgService: OrganizationService,
      private route: ActivatedRoute, private auth: AuthenticationService, private nav: NavigationService, private toastr: ToasterService, private registrationHelperService: RegistrationHelperService, private staticService: StaticDataService) {
    this.nav.pageTitle = 'Registration';
  }

  ngOnInit(): void {
    this.parentRouteSubscription = this.route.paramMap.subscribe((param) => {
      this.conferenceSlug = param.get('conferenceSlug');
      this.linkUniqueId = param.get('linkUniqueId');
      this.fetchData();
    });
  }

  fetchData() {
    this.orgService.getConferenceBySlugBasic(this.conferenceSlug).subscribe((conference) => {
      forkJoin([this.cmsService.getPackageGroupDiscountsOnly(conference.id, ''), this.staticService.getOrganizationCurrencyCode(conference.organizationId.toString())]).subscribe((results) => {
        this.conference = conference;
        this.hasGroupDiscounts = results[0].length > 0;
        this.currencyCode = results[1].value;

        this.analyticsService.setTitleAndLogPageView('Group Registration -' + this.conference.name);

        if (this.linkUniqueId) {
          var stringModel = new StringViewModel();
          stringModel.value = this.linkUniqueId;
          this.cmsService.getConferencePurchaseOrderForEditByLinkUniqueId(stringModel).subscribe((result) => {
            this.groupModel = result;
            this.registrationHelperService.initializeExistingPO(this.currencyCode, this.groupModel.id, this.groupModel.registrantPurchases);
            this.groupModel.registrantPurchases = this.registrationHelperService.getRegistrantPurchases();
            this.updatePOTotals();
            this.cmsService.getPackageGroupDiscountsOnly(conference.id, this.groupModel.id).subscribe((packages) => {
              this.hasGroupDiscounts = packages.length > 0;
            });
          },
          (error) => {
            this.editError = error.error[0];
          });
        } else {
          this.registrationHelperService.initialize(this.currencyCode);
          this.groupModel = new AddEditConferencePurchaseOrderViewModel();
          this.groupModel.conferenceId = this.conference.id;
          this.groupModel.registrantPurchases = this.registrationHelperService.getRegistrantPurchases();
          this.totals = new ConferencePurchaseOrderTotalsViewModel();
          this.totals.invoicedAmount = 0;
          this.initialized = true;
        }
      });
    });
  }

  showRegistrantModal(rowNumber: number | null) {
    const modalRef = this.modalService.open(GroupRegistrationRegistrantModalComponent, { size: 'lg', ariaLabelledBy: 'modal-basic-title', windowClass: 'flyout modal-xl-custom', backdropClass: 'flyout', backdrop: 'static' });
    modalRef.componentInstance.conference = this.conference;
    modalRef.componentInstance.rowNumber = rowNumber;
    modalRef.result.then(
      (result) => {
        this.groupModel.registrantPurchases = this.registrationHelperService.getRegistrantPurchases();
        this.updatePOTotals();
      },
      (reason) => {}
    );
  }

  updatePOTotals() {
    var model = new AddEditConferencePurchaseOrderViewModel();
    model.conferenceId = this.conference.id;
    model.promoCode = this.groupModel.promoCode;
    model.registrantPurchases = this.groupModel.registrantPurchases;
    model.id = this.groupModel.id ?? null;
    console.log(model);

    this.eventService.getGroupRegistrationTotals(this.conference.id, model).subscribe((result) => {
      this.totals = result;
      this.initialized = true;
    });
  }

  applyPromoCode() {
    this.checkPromoCode().subscribe((isValid) => {
      if (isValid) {
        this.updatePOTotals();
      }
    });
  }

  checkPromoCode(): Observable<boolean> {
    let model = new PromoCodeCheckViewModel();
    model.promoCode = this.groupModel.promoCode;
    model.packageIds = this.groupModel.registrantPurchases.map((x) => x.package.id);
    model.existingConferencePurchaseOrderId = this.groupModel.id ?? null;

    return this.eventService.checkPromoCodeForGroupRegistration(model).pipe(
        map((result) => {
            this.promoError = '';
            return true;
        }),
        catchError((error) => {
            this.promoError = error.error;
            return of(false);
        })
    );
}

  addRegistrant() {
    this.showRegistrantModal(null);
  }

  editRegistrant(rowNumber: number) {
    this.showRegistrantModal(rowNumber);
  }

  removeRegistrant(rowNumber: number, hasAdditionalTransactionsForUserPackage: boolean): void {
    if (hasAdditionalTransactionsForUserPackage) {
      this.toastr.messageDialog('You cannot delete this registrant because they have purchased additional package-related items. Reach out to support for assistance.', 'Cannot Remove Registrant').subscribe();
      return;
    }

    this.toastr.confirmDialog('Are you sure you want to delete this registrant?', 'Are you sure?').subscribe(result => {
      if (result) {
        this.registrationHelperService.removeRegistrantPurchase(rowNumber);
        this.groupModel.registrantPurchases = this.registrationHelperService.getRegistrantPurchases();
        this.updatePOTotals();
      }
    });
  }

  openGroupDiscountDetails() {
    const modalRef = this.modalService.open(ConferenceGroupDiscountsModalComponent, { size: 'lg', ariaLabelledBy: 'modal-basic-title', backdrop: 'static' });
      modalRef.componentInstance.conferenceId = this.conference.id;
      modalRef.componentInstance.conferencePurchaseOrderId = this.linkUniqueId ? this.groupModel.id : null;
      modalRef.result.then(
        (result) => {},
        (reason) => {}
      );
  }

  markAllTouched() {
    Object.keys(this.userFrm.controls).forEach((field) => {
      // {1}
      const control = this.userFrm.control.get(field);
      control.markAsTouched({onlySelf: true});
      control.markAsDirty({onlySelf: true}); // {3}
    });
  }

  purchasePackageClicked() {
    if (!this.userFrm.valid) {
      this.markAllTouched();
      this.errorMessage = 'There are some invalid or missing fields. Please fill out all required fields.';
      return;
    }

    if (this.groupModel.registrantPurchases.length === 0) {
      this.errorMessage = 'Must add at least one registrant.';
      return;
    }

    this.errorMessage = '';

    if (this.groupModel.promoCode) {
      this.checkPromoCode().subscribe((isValid) => {
        if (isValid) {
          this.finishSubmit();
        } else {
          this.errorMessage = 'There are some invalid or missing fields. Please fill out all required fields.';
          return;
        }
      });
    } else {
      this.finishSubmit();
    }

    
  }

  finishSubmit() {
    this.submitComplete = new Promise((resetButton: any, reject) => {
      if (this.groupModel.isCreditCard) {
        this.purchasePackage(resetButton);
      } else if (this.linkUniqueId) {
        this.updateConferencePurchaseOrder(resetButton);
      } else {
        this.eventService.isDuplicateConferencePurchaseOrder(this.conference.id, this.groupModel.contactEmail).subscribe((isDuplicate) => {
          if (isDuplicate) {
            this.toastr.confirmDialog('Are you sure you want to create a new purchase order? There is already an open PO for this email address.', 'Are you sure?').subscribe((confirm) => {
              if (confirm) {
                this.purchasePackageCheck(resetButton);
              } else {
                resetButton();
              }
            });
          } else {
            this.purchasePackageCheck(resetButton);
          }
        });
      }
    });
  }

  purchasePackageCheck(resetButton) {
    this.eventService.purchaseGroupRegistration(this.conference.id, this.groupModel).subscribe(
      (result) => {
        if (!result) {
          resetButton();
          this.errorMessage = 'Unable to process transaction';
          return;
        }
        resetButton();
        this.auth.logAttribution(this.conference.organizationId, this.totals.invoicedAmount);
        this.router.navigate(['../'], {relativeTo: this.route});
        this.toastr.messageDialog('Your purchase order has been completed. You will receive a confirmation email shortly', 'Thank You!').subscribe();
      },
      (error) => {
        resetButton();
        this.errorMessage = error.error[0];
      }
    );
  }

  purchasePackage(resetButton) {
    let card = {
      number: this.cardNumber,
      exp_month: this.expMo,
      exp_year: this.expYr,
      cvc: this.cvv
    };

    (<any>window).Stripe.card.createToken(card, (status: number, response: any) => {
      if (status === 200) {
        this.groupModel.cardToken = response.id;
        this.zone.run(() => {
          this.eventService.purchaseGroupRegistration(this.conference.id, this.groupModel).subscribe(
            (result) => {
              if (!result) {
                resetButton();
                this.errorMessage = 'Unable to process transaction';
                return;
              }
              resetButton();
              this.auth.logAttribution(this.conference.organizationId, this.totals.invoicedAmount);

              this.router.navigate(['../'], {relativeTo: this.route});

              this.toastr.messageDialog('Your purchase has been completed. You will receive a confirmation email shortly', 'Thank You!').subscribe();
            },
            (error) => {
              resetButton();
              this.errorMessage = error.error[0];
            }
          );
        });
      } else {
        resetButton();
        this.errorMessage = response.error.message;
      }
    });
  }

  updateConferencePurchaseOrder(resetButton) {
    this.eventService.updateConferencePurchaseOrder(this.groupModel.id, this.groupModel).subscribe(
      (result) => {
        if (!result) {
          resetButton();
          this.errorMessage = 'Unable to process transaction';
          return;
        }
        resetButton();
        this.router.navigate(['../../../'], {relativeTo: this.route});
        this.toastr.messageDialog('Your purchase order has been updated. You will receive an email with an updated invoice shortly', 'Thank You!').subscribe();
      },
      (error) => {
        resetButton();
        this.errorMessage = error.error[0];
      }
    );
  }
}
